import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { SPECIES, SERVICES } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, NativeSelect } from "@/components/common";
import { SlotPicker } from "@/components/SlotPicker";

const ENDPOINT = { public: "/public/bookings", vet: "/vet/bookings", admin: "/admin/bookings" };
const blank = {
  date: "", notturno: false, owner_name: "", owner_phone: "", owner_email: "", pickup_address: "", city: "",
  pet_name: "", species: "Cane", weight_kg: "", service_type: "singola", notes: "", metodo_pagamento: "Contanti / carta alla consegna",
};
const KLARNA_FEE = 6;

const Success = ({ b, onReset }) => (
  <div className="text-center py-6 rise" data-testid="booking-success">
    <CheckCircle2 className="h-14 w-14 text-[#B8860B] mx-auto" />
    <h3 className="font-display text-3xl font-semibold text-[#0D3B52] mt-4">Richiesta registrata</h3>
    <p className="text-sm text-[#5C6B73] mt-2">
      Ritiro il <strong>{fmtDate(b.date)}</strong>{b.notturno ? " (notturno)" : ""}.
      {b.maggiorazione > 0 && <span className="block mt-1 text-amber-800">Maggiorazione festivo/notturno: € {b.maggiorazione.toFixed(2)} + IVA</span>}
    </p>
    <p className="text-sm text-[#1C2A33] mt-4 max-w-md mx-auto">
      Ultimo passaggio: invia la conferma su WhatsApp all'agenzia, così riceviamo subito tutti i dettagli.
    </p>
    <a
      href={b.whatsapp_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="booking-whatsapp-button"
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1DA851] px-7 py-3.5 text-white font-semibold transition-colors"
    >
      <MessageCircle className="h-5 w-5" /> Invia conferma su WhatsApp
    </a>
    <div>
      <button type="button" onClick={onReset} data-testid="booking-new-button" className="mt-5 text-sm text-[#0D3B52] underline underline-offset-4">
        Nuova prenotazione
      </button>
    </div>
  </div>
);

export const BookingForm = ({ mode = "public", defaults = {}, onDone }) => {
  const [f, setF] = useState({ ...blank, ...defaults });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [privacy, setPrivacy] = useState(mode !== "public");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const ownerRequired = mode === "public";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.date) return toast.error("Seleziona il giorno del ritiro");
    if (!f.pickup_address.trim()) return toast.error("Inserisci l'indirizzo di ritiro");
    if (ownerRequired && (!f.owner_name.trim() || !f.owner_phone.trim())) return toast.error("Nome e telefono sono obbligatori");
    if (!privacy) return toast.error("È necessario accettare il trattamento dei dati");
    setBusy(true);
    try {
      const payload = { ...f, weight_kg: f.weight_kg === "" ? null : Number(f.weight_kg) };
      const { data } = await api.post(ENDPOINT[mode], payload);
      toast.success("Prenotazione registrata");
      if (mode === "admin") onDone?.(data);
      else { setDone(data); onDone?.(data); }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success b={done} onReset={() => { setDone(null); setF({ ...blank, ...defaults }); }} />;

  return (
    <form onSubmit={submit} className="grid gap-8 lg:grid-cols-2" data-testid={`booking-form-${mode}`}>
      <SlotPicker date={f.date} onDate={(v) => setF((p) => ({ ...p, date: v }))} notturno={f.notturno}
        onNotturno={(v) => setF((p) => ({ ...p, notturno: v }))} admin={mode === "admin"} />
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={ownerRequired ? "Nome e cognome *" : "Proprietario"}>
            <Input data-testid="booking-owner-name" value={f.owner_name} onChange={set("owner_name")} className="bg-white" />
          </Field>
          <Field label={ownerRequired ? "Telefono *" : "Telefono proprietario"}>
            <Input data-testid="booking-owner-phone" type="tel" value={f.owner_phone} onChange={set("owner_phone")} className="bg-white" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Indirizzo di ritiro *" className="sm:col-span-2">
            <Input data-testid="booking-address" value={f.pickup_address} onChange={set("pickup_address")} placeholder="Via, numero civico" className="bg-white" />
          </Field>
          <Field label="Città">
            <Input data-testid="booking-city" value={f.city} onChange={set("city")} className="bg-white" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Nome animale">
            <Input data-testid="booking-pet-name" value={f.pet_name} onChange={set("pet_name")} className="bg-white" />
          </Field>
          <Field label="Specie">
            <NativeSelect data-testid="booking-species" value={f.species} onChange={set("species")}>
              {SPECIES.map((s) => <option key={s}>{s}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Peso (kg)">
            <Input data-testid="booking-weight" type="number" min="0" step="0.1" value={f.weight_kg} onChange={set("weight_kg")} className="bg-white" />
          </Field>
        </div>
        <Field label="Servizio">
          <NativeSelect data-testid="booking-service" value={f.service_type} onChange={set("service_type")}>
            <option value="singola">{SERVICES.singola} (restituzione ceneri)</option>
            <option value="multipla">{SERVICES.multipla}</option>
            {mode === "admin" && <option value="smaltimento">{SERVICES.smaltimento}</option>}
          </NativeSelect>
        </Field>
        <Field label="Pagamento previsto" hint={f.metodo_pagamento.includes("Klarna") ? `Attenzione: con Klarna (paga in 3 rate) viene applicata una maggiorazione del ${KLARNA_FEE}% per coprire le commissioni del servizio.` : ""}>
          <NativeSelect data-testid="booking-payment" value={f.metodo_pagamento} onChange={set("metodo_pagamento")}>
            <option>Contanti / carta alla consegna</option><option>Bonifico anticipato</option><option>Klarna – paga in 3 rate (+{KLARNA_FEE}% commissioni)</option>
          </NativeSelect>
        </Field>
        <Field label="Note">
          <Textarea data-testid="booking-notes" rows={3} value={f.notes} onChange={set("notes")} placeholder="Piano, citofono, esigenze particolari…" className="bg-white" />
        </Field>
        {mode === "public" && (
          <label className="flex items-start gap-2 text-xs text-[#5C6B73] cursor-pointer">
            <Checkbox data-testid="booking-privacy" checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} className="mt-0.5" />
            Acconsento al trattamento dei dati personali per la gestione della richiesta (GDPR 679/2016).
          </label>
        )}
        <Button type="submit" disabled={busy} data-testid="booking-submit-button"
          className="w-full h-12 rounded-full bg-[#0D3B52] hover:bg-[#082535] text-white text-base">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma prenotazione ritiro"}
        </Button>
      </div>
    </form>
  );
};
